import { PlanogramProduct } from '../models/layout/planogram-product';
import { ProductDimensions } from '../models/layout/product-dimensions';
import { ProductOrientation } from '../models/layout/product-orientation';
import { ProductReference } from '../models/layout/product-reference';
import { PlanogramLayout } from '../models/layout/planogram-layout';
import { PlanogramShelf } from '../models/layout/planogram-shelf';
import { PlanogramDrop } from '../models/layout/planogram-drop';
import { PlanogramConstants } from '../constants';
import { Injectable } from '@angular/core';
import { PlanogramService } from '../services/planogram.service';
import { PlanogramProductStack } from '../models/layout/planogram-product-stack';

@Injectable({
  providedIn: 'root'
})
export class LayoutUtilitiesService {

  constructor(private planogramService: PlanogramService){}

  // Picks the base (un-oriented) dimensions for a placement. Tray (1) / case (2) variants use their
  // own dimensions; a unit - or a variant whose dims are missing - uses the single-unit dimensions.
  private getVariantBaseDimensions(
    productRef: ProductReference,
    caseConfig?: number | null
  ): ProductDimensions {
    if (caseConfig === 1 && productRef.trayWidth != null && productRef.trayHeight != null && productRef.trayDepth != null) {
      return { width: productRef.trayWidth, height: productRef.trayHeight, depth: productRef.trayDepth };
    }
    if (caseConfig === 2 && productRef.caseWidth != null && productRef.caseHeight != null && productRef.caseDepth != null) {
      return { width: productRef.caseWidth, height: productRef.caseHeight, depth: productRef.caseDepth };
    }
    return { ...productRef.dimensions };
  }

  // Calculates effective dimensions of a placement based on its reference, orientation and tray/case variant.
  calculateEffectiveDimensions(
    productRef: ProductReference,
    product: PlanogramProduct
  ): ProductDimensions {
    const baseDimensions = this.getVariantBaseDimensions(productRef, product.caseConfig);
    return this.applyOrientation(baseDimensions, product.orientation);
  }

  // Applies an orientation transform to a set of base (un-oriented) dimensions. Mutates and returns the
  // passed object, which is expected to be a fresh copy from getVariantBaseDimensions.
  private applyOrientation(
    effectiveDimensions: ProductDimensions,
    orientation: ProductOrientation
  ): ProductDimensions {
    let tempW, tempH, tempD;
    switch (orientation) {
      case ProductOrientation.SIDE:
        // Width = Depth, Depth = Width
        tempD = effectiveDimensions.width;
        effectiveDimensions.width = effectiveDimensions.depth;
        effectiveDimensions.depth = tempD;
        break;

      case ProductOrientation.FLATPACK:
        // Height = Depth, Depth = Height
        tempH = effectiveDimensions.height;
        effectiveDimensions.height = effectiveDimensions.depth;
        effectiveDimensions.depth = tempH;
        break;

      case ProductOrientation.FRONT_90:
        // Width = Height, Height = Width
        tempW = effectiveDimensions.width;
        effectiveDimensions.width = effectiveDimensions.height;
        effectiveDimensions.height = tempW;
        break;

      case ProductOrientation.SIDE_90:
        // First transform to SIDE, then do 90 degree rotation
        // SIDE: Width = Depth, Depth = Width
        tempW = effectiveDimensions.width;
        effectiveDimensions.width = effectiveDimensions.depth;
        effectiveDimensions.depth = tempW;
        // Then 90: Width = Height, Height = Width
        tempW = effectiveDimensions.width;
        effectiveDimensions.width = effectiveDimensions.height;
        effectiveDimensions.height = tempW;
        break;

      case ProductOrientation.FLATPACK_90:
        // First transform to FLATPACK, then do 90 degree rotation
        // FLATPACK: Height = Depth, Depth = Height
        tempH = effectiveDimensions.height;
        effectiveDimensions.height = effectiveDimensions.depth;
        effectiveDimensions.depth = tempH;
        // Then 90: Width = Height, Height = Width
        tempW = effectiveDimensions.width;
        effectiveDimensions.width = effectiveDimensions.height;
        effectiveDimensions.height = tempW;
        break;
    }

    return effectiveDimensions;
  }

  // Calculates total width filled by products on a shelf
  calculateShelfFilledWidth(layout: PlanogramLayout, shelf: PlanogramShelf): number {
  let filledWidth = 0;

  for (const stack of shelf.stacks) {
    filledWidth += this.calculateStackWidth(stack, layout);
  }

  return filledWidth;
}

  // Calculates total height of a drop including shelf spacing and lip heights
  calculateDropTotalHeight(
    drop: PlanogramDrop,
    shelfLipHeight: number,
    minShelfSpacing: number,
    layout?: PlanogramLayout
  ): number {
    // Add shelf lip heights and minimum spacing between shelves
    const totalHeight = drop.shelves.reduce((sum, shelf) => {
      let shelfHeight = this.calculateMaxShelfHeight(shelf, layout);
      return sum + shelfHeight + shelfLipHeight + minShelfSpacing;
    }, 0);
    
    // Subtract one minimum spacing since it's not needed after the last shelf
    return totalHeight - minShelfSpacing;
  }

  // Calculates maximum height among all products on a shelf
  // When layout is provided, calculates minimum possible height considering vertical squeeze
  calculateMaxShelfHeight(shelf: PlanogramShelf, layout?: PlanogramLayout): number {
    let maxHeight = 0;

    shelf.stacks.forEach((stack) => {
      let stackHeight = 0;
      stack.products.forEach((product) => {
        if (layout) {
          // Calculate squeezed height when layout is provided
          const productRef = layout.productReference.get(product.productId);
          if (productRef) {
            const effectiveDims = this.calculateEffectiveDimensions(productRef, product);
            const squeezedHeight = effectiveDims.height * (1 - (productRef.squeezeH || 0));
            stackHeight += squeezedHeight * product.stackHeight;
          }
        } else {
          // Fallback to stored dimensions
          stackHeight += product.dimensions!.height * product.stackHeight;
        }
      });
      if(stackHeight > maxHeight)
        maxHeight = stackHeight;
    });
    
    return Math.max(maxHeight, shelf.minHeight ?? 0);
  }

  // Calculates new shelf width after adding or removing a product
  calculateNewShelfWidth(
    shelf: PlanogramShelf,
    productWidth: number,
    isAdding: boolean = true
  ): number {
    return isAdding 
      ? shelf.filledWidth + productWidth 
      : shelf.filledWidth - productWidth;
  }

  // Calculates the width of a product stack
  calculateStackWidth(
    stack: PlanogramProductStack,
    layout: PlanogramLayout
  ): number {
    let maxWidth = 0;

    for (const product of stack.products) {
      const productRef = layout.productReference.get(product.productId);
      if (!productRef) continue;

      const effectiveDims = this.calculateEffectiveDimensions(productRef, product);
      const productWidth = effectiveDims.width * (1 - (productRef.squeezeW || 0));

      maxWidth = Math.max(maxWidth, productWidth);
    }

    return maxWidth;
  }

  // Calculates the height of a product stack
  calculateStackHeight(
    stack: PlanogramProductStack,
    layout: PlanogramLayout
  ): number {
    let maxHeight = 0;

    for (const product of stack.products) {
      const productRef = layout.productReference.get(product.productId);
      if (!productRef) continue;

      const effectiveDims = this.calculateEffectiveDimensions(productRef, product);

      maxHeight += effectiveDims.height * product.stackHeight;
    }

    return maxHeight;
  }

  // Calculates effective product width considering orientation and squeeze factor
  calculateProductWidth(
    productRef: ProductReference,
    product: PlanogramProduct
  ): number {
    const effectiveDimensions = this.calculateEffectiveDimensions(productRef, product);
    return effectiveDimensions.width * (1 - (productRef.squeezeW || 0));
  }

  // Calculates product height considering orientation and stack height
  calculateProductHeight(
    productRef: ProductReference,
    product: PlanogramProduct
  ): number {
    const effectiveDimensions = this.calculateEffectiveDimensions(productRef, product);
    return effectiveDimensions.height * product.stackHeight;
  }

  // Calculate shelf spacing distribution for a drop
  calculateDropShelfSpacing(
    drop: PlanogramDrop,
    minSpacing: number,
  ): void {
    if (drop.shelves.length <= 1) return;
    
    const defaultDropHeight = this.planogramService.getDropHeight();
    const defaultDropFooterHeight = this.planogramService.getDropFooterHeight();

    const availableSpace = 
      (drop.height ?? defaultDropHeight) - (drop.footerHeight ?? defaultDropFooterHeight) - drop.totalFilledHeight;

    if (availableSpace < 0) {
      drop.shelves.forEach((shelf, index) => {
        shelf.shelfSpacing = index === 0 ? minSpacing : 0;
      });
      return;
    }

    drop.shelves[0].shelfSpacing = minSpacing;
    
    const numGaps = drop.shelves.length - 1;
    const extraSpacingPerGap = numGaps > 0 ? Math.floor(availableSpace / numGaps) : 0;

    drop.shelves.forEach((shelf, index) => {
      if (index > 0) {
        shelf.shelfSpacing = minSpacing + extraSpacingPerGap;
      }
    });

    // Adjust final spacing
    let accumulatedHeight = minSpacing;
    drop.shelves.forEach(shelf => {
      accumulatedHeight += shelf.shelfHeight + shelf.shelfSpacing;
    });

    const lastShelf = drop.shelves[drop.shelves.length - 1];
    const heightDifference = (drop.height ?? defaultDropHeight) - (drop.footerHeight ?? defaultDropFooterHeight) - accumulatedHeight;
    lastShelf.shelfSpacing += heightDifference;

    if (lastShelf.shelfSpacing < 0) {
      drop.shelves.forEach(shelf => {
        shelf.shelfSpacing = 0;
      });
    }
  }

  // Calculate product DOS for a single product instance
  calculateProductDOS(
    product: PlanogramProduct,
    productRef: ProductReference,
    shelfDepth: number = PlanogramConstants.DEFAULT_SHELF_DEPTH
  ): number {
    const effectiveDimensions = this.calculateEffectiveDimensions(productRef, product);
    if (productRef.salesData.salesQty == 0 || effectiveDimensions.depth == 0) return Number.NaN;
    const dFacings = Math.floor(shelfDepth / effectiveDimensions.depth);
    const salesQty = productRef.salesData.salesQty ?? 0;
    // A tray/case facing holds Tray/CaseCapacity units; a unit facing holds 1. Keeps the live
    // DOS consistent with the Excel Capacity Report (which counts units, not containers).
    const capacityFactor = product.caseConfig === 1 ? (productRef.trayCapacity ?? 1)
      : product.caseConfig === 2 ? (productRef.caseCapacity ?? 1)
      : 1;
    return (dFacings * product.stackHeight * capacityFactor) / (salesQty / 7);
  }

  // Calculate layout breakdown for products, how many facings each product has (not including vertical)
  calculateLayoutBreakdown(
    layout: PlanogramLayout,
    planogramItems: Map<number, ProductReference>
  ): Map<number, number> {
    const breakdown = new Map<number, number>();
    
    planogramItems.forEach(p => breakdown.set(p.productId, 0));
    
    layout.drops.forEach(drop => {
      drop.shelves.forEach(shelf => {
        shelf.stacks.forEach((stack) => {
          // collect unique productIds within the stack
          const uniqueProductIds = new Set(stack.products.map(p => p.productId));

          // count each unique product only once per stack
          uniqueProductIds.forEach(productId => {
            const current = breakdown.get(productId) ?? 0;
            breakdown.set(productId, current + 1);
          });
        });
      });
    });
    
    return breakdown;
  }

  // Calculate DOS breakdown for layout
  calculateLayoutDOSBreakdown(
    layout: PlanogramLayout,
    planogramItems: Map<number, ProductReference>
  ): Map<number, number> {
    const breakdown = new Map<number, number>();
    
    planogramItems.forEach(p => breakdown.set(p.productId, 0));
    
    layout.drops.forEach(drop => {
      drop.shelves.forEach(shelf => {
        const shelfDepth = shelf.shelfDepth ?? drop.depth ?? this.planogramService.getDropDepth();
        shelf.stacks.forEach((stack) => {
          stack.products.forEach(product => {
            const productRef = planogramItems.get(product.productId);
            if (!productRef) return;
            
            const current = breakdown.get(product.productId) ?? 0;
            const dos = this.calculateProductDOS(product, productRef, shelfDepth);
            breakdown.set(product.productId, current + dos);
          });
          });
      });
    });
    
    return breakdown;
  }

  // Calculate live depth-facings (units packed front-to-back) per product for
  // the layout. Derived from the live shelf depth so it stays consistent with
  // the DOS breakdown instead of the stored facingDepth snapshot. Returns the
  // min/max across placements (a product can sit on shelves of differing depth).
  calculateLayoutDFacingsBreakdown(
    layout: PlanogramLayout,
    planogramItems: Map<number, ProductReference>
  ): Map<number, { min: number; max: number }> {
    const breakdown = new Map<number, { min: number; max: number }>();

    layout.drops.forEach(drop => {
      drop.shelves.forEach(shelf => {
        const shelfDepth = shelf.shelfDepth ?? drop.depth ?? this.planogramService.getDropDepth();
        shelf.stacks.forEach((stack) => {
          stack.products.forEach(product => {
            const productRef = planogramItems.get(product.productId);
            if (!productRef) return;

            const effectiveDimensions = this.calculateEffectiveDimensions(productRef, product);
            if (effectiveDimensions.depth === 0) return;

            const dFacings = Math.floor(shelfDepth / effectiveDimensions.depth);
            const existing = breakdown.get(product.productId);
            if (!existing) {
              breakdown.set(product.productId, { min: dFacings, max: dFacings });
            } else {
              existing.min = Math.min(existing.min, dFacings);
              existing.max = Math.max(existing.max, dFacings);
            }
          });
        });
      });
    });

    return breakdown;
  }

  // Calculate the on-shelf capacity (selling units the placed facings hold) per
  // product for the whole layout. Each placed product is one horizontal facing;
  // its individual capacity is (stack height) x (depth facings on its shelf),
  // because stack height and shelf depth can differ per facing. We sum every
  // placement, then scale by the product's per-unit pack capacity
  // (`productRef.capacity`, e.g. 12 for a case-of-12) which is a no-op (x1) for
  // products merchandised as eaches. The result is compared against CasePack by
  // the Case Pack overlay/label.
  calculateLayoutCapacityBreakdown(
    layout: PlanogramLayout,
    planogramItems: Map<number, ProductReference>
  ): Map<number, number> {
    const breakdown = new Map<number, number>();

    planogramItems.forEach(p => breakdown.set(p.productId, 0));

    layout.drops.forEach(drop => {
      drop.shelves.forEach(shelf => {
        const shelfDepth = shelf.shelfDepth ?? drop.depth ?? this.planogramService.getDropDepth();
        shelf.stacks.forEach((stack) => {
          stack.products.forEach(product => {
            const productRef = planogramItems.get(product.productId);
            if (!productRef) return;

            const effectiveDimensions = this.calculateEffectiveDimensions(productRef, product);
            if (effectiveDimensions.depth === 0) return;

            const dFacings = Math.floor(shelfDepth / effectiveDimensions.depth);
            const perUnitCapacity = productRef.capacity ?? 1;
            const facingCapacity = product.stackHeight * dFacings * perUnitCapacity;

            const current = breakdown.get(product.productId) ?? 0;
            breakdown.set(product.productId, current + facingCapacity);
          });
        });
      });
    });

    return breakdown;
  }

  // Calculate effective width for a list of products
  calculateProductsWidth(
    products: PlanogramProduct[], 
    productReferences: Map<number, ProductReference>
  ): number {
    return products.reduce((total, product) => {
      const productRef = productReferences.get(product.productId);
      if (!productRef) return total;
      return total + this.calculateProductWidth(productRef, product);
    }, 0);
  }

  // Validate if a shelf has enough remaining width for a product
  hasEnoughShelfSpace(
    shelf: PlanogramShelf,
    product: PlanogramProduct,
    productRef: ProductReference,
    shelfWidth: number = PlanogramConstants.DEFAULT_SHELF_WIDTH
  ): boolean {
    const productWidth = this.calculateProductWidth(productRef, product);
    return (shelf.filledWidth + productWidth) <= shelfWidth;
  }

  // Get product's total effective volume (considering orientation and stack height)
  calculateProductVolume(
    product: PlanogramProduct,
    productRef: ProductReference
  ): number {
    const effectiveDimensions = this.calculateEffectiveDimensions(productRef, product);
    return effectiveDimensions.width * 
          effectiveDimensions.height * 
          effectiveDimensions.depth * 
          product.stackHeight;
  }

  // Calculate shelf capacity for a given product (considering orientation)
  calculateShelfCapacity(
    shelf: PlanogramShelf,
    productRef: ProductReference,
    orientation: ProductOrientation
  ): {
    horizontalCapacity: number;
    verticalCapacity: number;
    depthCapacity: number;
  } {
    // findOptimalOrientation explores hypothetical orientations on a bare reference (no placement),
    // so there is no caseConfig here - use the single-unit base dimensions.
    const effectiveDimensions = this.applyOrientation(this.getVariantBaseDimensions(productRef, null), orientation);
    const shelfWidth = shelf.sheldWidth ?? PlanogramConstants.DEFAULT_SHELF_WIDTH;
    const shelfDepth = shelf.shelfDepth ?? PlanogramConstants.DEFAULT_SHELF_DEPTH;

    return {
      horizontalCapacity: Math.floor(shelfWidth / effectiveDimensions.width),
      verticalCapacity: Math.floor(shelf.shelfHeight / effectiveDimensions.height),
      depthCapacity: Math.floor(shelfDepth / effectiveDimensions.depth)
    };
  }

  // Check if a product fits within shelf height constraints
  productFitsShelfHeight(
    shelf: PlanogramShelf,
    product: PlanogramProduct,
    productRef: ProductReference
  ): boolean {
    const effectiveDimensions = this.calculateEffectiveDimensions(productRef, product);
    const totalHeight = effectiveDimensions.height * product.stackHeight;
    return totalHeight <= shelf.shelfHeight;
  }

  // Calculate shelf utilization percentage
  calculateShelfUtilization(
    shelf: PlanogramShelf,
    shelfWidth: number = PlanogramConstants.DEFAULT_SHELF_WIDTH
  ): number {
    if (shelfWidth === 0) return 0;
    return (shelf.filledWidth / shelfWidth) * 100;
  }

  // Find optimal product orientation to maximize shelf capacity
  findOptimalOrientation(
    shelf: PlanogramShelf,
    productRef: ProductReference
  ): ProductOrientation {
    let maxCapacity = 0;
    let optimalOrientation = productRef.orientation;

    const orientations = [
      ProductOrientation.FRONT,
      ProductOrientation.SIDE,
      ProductOrientation.FLATPACK,
      ProductOrientation.FRONT_90,
      ProductOrientation.SIDE_90,
      ProductOrientation.FLATPACK_90
    ];

    orientations.forEach(orientation => {
      const capacity = this.calculateShelfCapacity(shelf, productRef, orientation);
      const totalCapacity = capacity.horizontalCapacity * 
                          capacity.verticalCapacity * 
                          capacity.depthCapacity;
      
      if (totalCapacity > maxCapacity) {
        maxCapacity = totalCapacity;
        optimalOrientation = orientation;
      }
    });

    return optimalOrientation;
  }

  // Calculate total shelf space utilization for a drop
  calculateDropUtilization(
    drop: PlanogramDrop,
    shelfWidth: number = PlanogramConstants.DEFAULT_SHELF_WIDTH
  ): number {
    if (drop.shelves.length === 0) return 0;
    
    const totalUtilization = drop.shelves.reduce((sum, shelf) => 
      sum + this.calculateShelfUtilization(shelf, shelfWidth), 0);
      
    return totalUtilization / drop.shelves.length;
  }
}